import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import type { ComplianceRule, FlagType } from '../../types';

interface RuleDef {
  label: string;
  severity: 'HARD_BLOCK' | 'SOFT_WARNING';
  description: string;
}

const RULE_DEFS: Record<string, RuleDef> = {
  PROFANITY:               { label: 'Profanity Detection',       severity: 'HARD_BLOCK',   description: 'Blocks videos containing profanity or offensive language.' },
  COMPETITOR_MENTION:      { label: 'Competitor Mention',        severity: 'HARD_BLOCK',   description: 'Flags direct mentions of competitor brands or vehicles.' },
  VERBAL_PRICING:          { label: 'Verbal Pricing Claims',     severity: 'HARD_BLOCK',   description: 'Detects specific pricing, financing, or payment commitments.' },
  DISCRIMINATORY_LANGUAGE: { label: 'Discriminatory Language',   severity: 'HARD_BLOCK',   description: 'Flags language that may be discriminatory or exclusionary.' },
  PII_DETECTED:            { label: 'PII Detection',             severity: 'HARD_BLOCK',   description: 'Identifies phone numbers, SSNs, credit scores, or other PII.' },
  VIN_MISMATCH:            { label: 'VIN Mismatch',              severity: 'HARD_BLOCK',   description: 'Checks that the mentioned VIN matches the deal record.' },
  NEGATIVE_SENTIMENT:      { label: 'Negative Sentiment',        severity: 'SOFT_WARNING', description: 'Warns when reps mention delays, complaints, or dealership issues.' },
  LOW_VQS_SCORE:           { label: 'Low VQS Score',             severity: 'SOFT_WARNING', description: 'Prompts review when composite score falls below threshold.' },
  EXCESSIVE_FILLERS:       { label: 'Excessive Filler Words',    severity: 'SOFT_WARNING', description: 'Warns when filler word density exceeds 15% of speech.' },
};

const ALL_FLAG_TYPES = Object.keys(RULE_DEFS) as FlagType[];

type EffectiveRule = {
  flagType: FlagType;
  isEnabled: boolean;
  hasOverride: boolean;
  override: ComplianceRule | null;
};

const GroupAdminCompliancePage: React.FC = () => {
  const [overrides, setOverrides] = useState<ComplianceRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    api.get('/group-admin/compliance-rules')
      .then((res) => setOverrides(res.data.data ?? []))
      .finally(() => setLoading(false));
  }, []);

  const overrideMap = Object.fromEntries(overrides.map((r) => [r.flagType, r]));

  const effectiveRules: EffectiveRule[] = ALL_FLAG_TYPES.map((flagType) => {
    const override = overrideMap[flagType] ?? null;
    return {
      flagType,
      isEnabled: override ? override.isEnabled : true,
      hasOverride: override !== null,
      override,
    };
  });

  const hardBlocks = effectiveRules.filter((r) => RULE_DEFS[r.flagType]?.severity === 'HARD_BLOCK');
  const softWarnings = effectiveRules.filter((r) => RULE_DEFS[r.flagType]?.severity === 'SOFT_WARNING');

  const handleToggle = async (flagType: FlagType, currentEnabled: boolean) => {
    setToggling(flagType);
    try {
      const res = await api.put(`/group-admin/compliance-rules/${flagType}`, {
        isEnabled: !currentEnabled,
      });
      const updated = res.data.data as ComplianceRule;
      setOverrides((prev) => {
        const exists = prev.some((r) => r.flagType === flagType);
        return exists
          ? prev.map((r) => r.flagType === flagType ? updated : r)
          : [...prev, updated];
      });
      toast.success(`${RULE_DEFS[flagType]?.label ?? flagType} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update rule');
    } finally {
      setToggling(null);
    }
  };

  const handleReset = async (flagType: FlagType) => {
    setToggling(flagType);
    try {
      await api.delete(`/group-admin/compliance-rules/${flagType}`);
      setOverrides((prev) => prev.filter((r) => r.flagType !== flagType));
      toast.success('Rule reset to platform default');
    } catch {
      toast.error('Failed to reset rule');
    } finally {
      setToggling(null);
    }
  };

  const RuleSection: React.FC<{ title: string; rules: EffectiveRule[]; badgeClass: string; badgeLabel: string }> = ({ title, rules, badgeClass, badgeLabel }) => (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden mb-6">
      <div className="px-8 py-5 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">{title}</h2>
        <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${badgeClass}`}>
          {badgeLabel}
        </span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100 dark:border-slate-800">
            <th className="text-left px-8 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Rule</th>
            <th className="text-left px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hidden md:table-cell">Description</th>
            <th className="text-left px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Source</th>
            <th className="text-center px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Enabled</th>
            <th className="text-right px-8 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rules.map((rule) => {
            const def = RULE_DEFS[rule.flagType];
            const isBusy = toggling === rule.flagType;
            return (
              <tr key={rule.flagType} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                <td className="px-8 py-5">
                  <span className="font-bold text-slate-900 dark:text-white text-sm">{def?.label ?? rule.flagType}</span>
                </td>
                <td className="px-6 py-5 hidden md:table-cell">
                  <span className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">{def?.description}</span>
                </td>
                <td className="px-6 py-5">
                  {rule.hasOverride ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-primary/10 dark:bg-primary/20 text-primary border border-primary/20">
                      <span className="material-symbols-outlined text-[11px]">edit</span>
                      Group Override
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                      <span className="material-symbols-outlined text-[11px]">shield</span>
                      Platform Default
                    </span>
                  )}
                </td>
                <td className="px-6 py-5 text-center">
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => void handleToggle(rule.flagType as FlagType, rule.isEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 disabled:opacity-50 cursor-pointer ${
                      rule.isEnabled ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition-transform duration-300 ${
                      rule.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </td>
                <td className="px-8 py-5 text-right">
                  {rule.hasOverride ? (
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void handleReset(rule.flagType as FlagType)}
                      className="text-xs font-bold text-slate-400 hover:text-primary transition-colors disabled:opacity-40 underline underline-offset-2 cursor-pointer"
                    >
                      Reset
                    </button>
                  ) : (
                    <span className="text-[10px] font-bold text-slate-200 dark:text-slate-700 uppercase tracking-widest">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
            Compliance Rules
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Effective rules across your group · group overrides take precedence over platform defaults
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
            <span className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Loading rules...</span>
          </div>
        ) : (
          <>
            <div className="p-4 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-xl flex items-start gap-3 mb-8">
              <span className="material-symbols-outlined text-[18px] text-blue-500 shrink-0 mt-0.5">info</span>
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 leading-relaxed">
                Rules shown with <span className="font-black">Platform Default</span> source inherit the platform setting. Toggling any rule creates a group-level override. Use <span className="font-black">Reset</span> to revert a rule back to its platform default.
              </p>
            </div>

            <RuleSection
              title="Hard Block Rules"
              rules={hardBlocks}
              badgeClass="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30"
              badgeLabel="Hard Block"
            />
            <RuleSection
              title="Soft Warning Rules"
              rules={softWarnings}
              badgeClass="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-100 dark:border-yellow-900/30"
              badgeLabel="Soft Warning"
            />
          </>
        )}
      </main>
    </div>
  );
};

export default GroupAdminCompliancePage;
